declare global {
  interface HTMLElementTagNameMap {
    "product-bnpl": ProductBnpl;
  }
  // `Window.theme` is declared once, in src/types/theme.d.ts.
}

type JQueryLike = ((selector: string | Document | HTMLElement) => {
  on: (
    event: string,
    handler: (e: unknown, variant?: ShopifyVariant) => void,
  ) => void;
}) & {
  fn?: unknown;
};

type ShopifyVariant = {
  id?: number;
  price?: number;
  available?: boolean;
};

const MODAL_OPEN_CLASS = "is-open";

class ProductBnpl extends HTMLElement {
  private amountEl: HTMLElement | null = null;
  private wrapperEl: HTMLElement | null = null;
  private trigger: HTMLElement | null = null;
  private modal: HTMLElement | null = null;
  private overlay: HTMLElement | null = null;
  private closeButton: HTMLElement | null = null;
  private readonly instalments = 4;
  private currentPriceCents = 0;
  private lastFocused: HTMLElement | null = null;

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && this.isModalOpen) {
      this.closeModal();
    }
  };

  connectedCallback(): void {
    this.currentPriceCents = Number(this.getAttribute("data-price-cents")) || 0;

    this.wrapperEl = this.querySelector("[data-bnpl-wrapper]");
    this.amountEl = this.querySelector("[data-bnpl-amount]");
    this.trigger = this.querySelector("[data-bnpl-trigger]");

    const modalId = this.getAttribute("data-bnpl-modal-id");
    if (modalId) {
      this.modal = document.getElementById(modalId);
      this.overlay = this.modal?.querySelector("[data-bnpl-overlay]") ?? null;
      this.closeButton = this.modal?.querySelector("[data-bnpl-close]") ?? null;
    }

    this.trigger?.addEventListener("click", (e) => {
      e.preventDefault();
      this.openModal();
    });
    this.trigger?.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.openModal();
      }
    });
    this.overlay?.addEventListener("click", () => this.closeModal());
    this.closeButton?.addEventListener("click", (e) => {
      e.preventDefault();
      this.closeModal();
    });

    document.addEventListener("keydown", this.onKeyDown);

    this.bindVariantChange();
    this.updateVisibility();
  }

  disconnectedCallback(): void {
    document.removeEventListener("keydown", this.onKeyDown);
  }

  private bindVariantChange(): void {
    const tryBind = (): boolean => {
      const jq = (window as unknown as { jQuery?: JQueryLike }).jQuery;
      if (!jq) return false;
      jq(document).on("variantChanged", (_e, variant) => {
        if (variant && typeof variant.price === "number") {
          this.setPrice(variant.price, variant.available !== false);
        }
      });
      return true;
    };

    if (tryBind()) return;

    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts++;
      if (tryBind() || attempts > 50) window.clearInterval(interval);
    }, 100);
  }

  private setPrice(priceCents: number, available: boolean): void {
    this.currentPriceCents = priceCents;
    this.renderAmount();
    this.updateVisibility(available);
  }

  private updateVisibility(available?: boolean): void {
    if (!this.wrapperEl) return;

    if (available === undefined) {
      if (this.currentPriceCents <= 0) {
        this.wrapperEl.hidden = true;
      }
      return;
    }

    const shouldShow = available && this.currentPriceCents > 0;
    this.wrapperEl.hidden = !shouldShow;
  }

  private renderAmount(): void {
    if (!this.amountEl || this.instalments <= 0) return;
    const perInstalmentCents = Math.floor(
      this.currentPriceCents / this.instalments,
    );
    this.amountEl.innerHTML = this.formatMoney(perInstalmentCents);
  }

  private formatMoney(amountCents: number): string {
    const fmt = window.theme?.Shopify?.formatMoney;
    const format =
      window.theme?.money_format_with_code_preference ||
      window.theme?.money_format;
    if (typeof fmt === "function") {
      try {
        return fmt(amountCents, format);
      } catch {
        // fall through to local formatter
      }
    }
    return this.fallbackFormat(amountCents);
  }

  private fallbackFormat(amountCents: number): string {
    const dollars = amountCents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  private get isModalOpen(): boolean {
    return this.modal?.classList.contains(MODAL_OPEN_CLASS) ?? false;
  }

  private openModal(): void {
    if (!this.modal) return;
    this.lastFocused = document.activeElement as HTMLElement | null;
    this.modal.classList.add(MODAL_OPEN_CLASS);
    this.modal.setAttribute("aria-hidden", "false");
    document.documentElement.style.overflow = "hidden";
    this.closeButton?.focus();
  }

  private closeModal(): void {
    if (!this.modal) return;
    this.modal.classList.remove(MODAL_OPEN_CLASS);
    this.modal.setAttribute("aria-hidden", "true");
    document.documentElement.style.overflow = "";
    this.lastFocused?.focus();
  }
}

if (!window.customElements.get("product-bnpl")) {
  window.customElements.define("product-bnpl", ProductBnpl);
}

export { ProductBnpl };
